import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import ThemeToggle from '../components/ThemeToggle';
import './About.css';

const PRIVACY_URL = 'https://gobunnyy.com/privacy-policy';
const TERMS_URL = 'https://gobunnyy.com/terms-of-use';
const COMPANY_URL = 'https://gobunnyy.com';

/**
 * Public product page for OAuth brand verification — /about (no login).
 * Must stay crawlable without auth and name the app "GoDash by GoBunnyy".
 */
const About = () => {
  useEffect(() => {
    document.title = 'About · GoDash by GoBunnyy';
  }, []);

  return (
    <div className="about-page">
      <div className="about-atmosphere" aria-hidden="true">
        <span className="about-orb about-orb--a" />
        <span className="about-orb about-orb--b" />
        <span className="about-grid" />
      </div>

      <header className="about-topbar">
        <Link to="/about" className="about-brand-link" aria-label="GoDash by GoBunnyy">
          <BrandLogo variant="navbar" />
        </Link>
        <div className="about-topbar-actions">
          <ThemeToggle />
          <Link to="/login" className="btn btn-primary about-signin-btn">
            Sign in
          </Link>
        </div>
      </header>

      <main className="about-main">
        <section className="about-hero" aria-labelledby="about-app-name">
          <p className="about-eyebrow">Product overview</p>
          <h1 id="about-app-name">GoDash by GoBunnyy</h1>
          <p className="about-lead">
            GoDash is the consulting and career-services workspace from GoBunnyy. Teams use it to
            run jobs, candidates, CRM, services, and operations in one place—with Google Sign-In and
            Calendar so scheduling and account activity stay aligned.
          </p>
          <div className="about-hero-actions">
            <Link to="/login" className="btn btn-primary">
              Sign in to GoDash
            </Link>
            <a
              href={COMPANY_URL}
              className="btn btn-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              About GoBunnyy
            </a>
          </div>
        </section>

        <section className="about-purpose" aria-labelledby="about-purpose-heading">
          <h2 id="about-purpose-heading">What GoDash is for</h2>
          <p>
            GoDash helps GoBunnyy consultants and career specialists manage day-to-day delivery work:
            tracking roles and applicants, coordinating client interactions, booking appointments,
            managing service packages, and keeping finance and project activity organized.
          </p>
          <ul className="about-feature-list">
            <li>
              <strong>Recruiting &amp; career ops</strong> — jobs, resumes, matches, candidates, and
              timesheets
            </li>
            <li>
              <strong>CRM &amp; services</strong> — pipeline, customer interactions, booking pages,
              and service delivery
            </li>
            <li>
              <strong>Google account access</strong> — sign in with Google; Calendar and profile
              scopes keep CRM scheduling and user identity in sync
            </li>
            <li>
              <strong>Operations</strong> — orders, vendors, projects, and internal settings for
              authorized team members
            </li>
          </ul>
        </section>

        <section className="about-google" aria-labelledby="about-google-heading">
          <h2 id="about-google-heading">How we use Google data</h2>
          <p>
            When you authorize GoDash, we use your Google account to authenticate you and—when you
            grant Calendar access—to read and create calendar events related to CRM appointments and
            service activity. We use profile information such as name, email, and photo to identify
            your workspace account. Use of Google user data is limited to providing these features,
            as described in our privacy policy.
          </p>
        </section>
      </main>

      <footer className="about-footer">
        <nav className="about-legal" aria-label="Legal">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span className="about-legal-sep" aria-hidden="true">
            ·
          </span>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
          <span className="about-legal-sep" aria-hidden="true">
            ·
          </span>
          <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">
            gobunnyy.com
          </a>
        </nav>
        <p className="about-footer-note">GoDash by GoBunnyy — consulting &amp; career services</p>
      </footer>
    </div>
  );
};

export default About;
